/* Built-in Libraries */

import { CheckCircleIcon, ChevronDownIcon, InformationCircleIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router';
import { updateApproval } from '@/api/approvals';
import { deleteInvoice } from '@/api/Sherpa/Draw';
import { getProjectTeamMembers } from '@/api/Sherpa/Project/modules/ProjectTeam';
/* Custom Components*/
import AccentTitle from '@/components/UI/AccentTitle';
import { Accordion, AccordionItemT2 } from '@/components/UI/Accordion';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import DropDown, { type DropDownMenuItem } from '@/components/UI/DropDown';
import Input from '@/components/UI/Form/Input';
import Select from '@/components/UI/Form/Select';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import Confirm from '@/components/UI/Modal/Confirm';
import Table from '@/components/UI/Table';
import { approvalAction } from '@/constants/Approval';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { useInvoices } from '@/hooks/ReactQueryHooks/useProjectDrawQuery';
/* Hooks And Miscellaneous*/
import { useProject } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { authSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { UpdateApprovalPayload } from '@/types/Approvals';
import { convertEnumToText, formatNumber, kFormatter, sanitizeResponseError } from '@/utils';
import { NewInvoices } from '../NewInvoices';

const defaultParams = {
  perPage: 10,
  page: 1,
};

const ApprovalActions = { ...approvalAction, DELETE: 'Delete' };

interface ISelectedInvoice {
  id: number;
  createdBy: string;
  approver: string;
  status: string;
  approvalId: number;
  note?: string;
  assignedUserId?: number;
  invoiceNumber?: number;
  selectedId: string;
}

export const InvoicesList = ({
  totalInvoiceApprovedAmount,
  totalInvoiceAmount,
}: {
  totalInvoiceApprovedAmount: number;
  totalInvoiceAmount: number;
}) => {
  const { projectId, drawId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [selectedInvoices, setSelectedInvoices] = useState<ISelectedInvoice[]>([]);

  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [showApproveModal, setShowApproveModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [list, setList] = useState<any[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<{
    id: number;
    invoiceNumber: number;
  } | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [params, setParams] = useState<any>(defaultParams);
  const tableRef = useRef<any>(null);

  const auth = useSelector(authSelector);

  const fetchInvoices = useInvoices({
    projectId: Number(projectId),
    drawId: Number(drawId),
    params,
  });
  const invoices = fetchInvoices?.data?.data || [];
  const invoiceCounts = fetchInvoices?.data?.meta?.count || 0;
  const totalInvoiceApproved = fetchInvoices?.data?.meta?.totalApproved || 0;
  const pageCount = Math.ceil(invoiceCounts / params.perPage);

  useEffect(() => {
    if (invoices?.length > 0) {
      const formattedData = invoices?.map((d: any) => {
        let trashIcon = false;
        if (d?.status === 'PENDING' || d?.status === 'REJECTED') {
          if (auth.data.name === d.createdBy) {
            trashIcon = true;
          }
        }
        return { ...d, trashIcon };
      });
      setList(formattedData);
    } else {
      setList([]);
    }
  }, [invoices, auth.data.name]);

  const fetchProject = useProject({ projectId: Number(projectId) });
  const project = fetchProject?.data?.data;

  const handleModel = () => {
    setShowModal(!showModal);
  };

  const removeInvoiceHandler = ({ id, invoiceNumber }: { id: number; invoiceNumber: number }) => {
    setShowRemoveConfirm(true);
    setSelectedInvoice({ id, invoiceNumber });
  };

  const cancelRemoveInvoice = () => {
    setShowRemoveConfirm(false);
  };

  const removeInvoice = useMutation(
    async (invoiceId: number) =>
      await deleteInvoice({
        projectId: Number(projectId),
        drawId: Number(drawId),
        invoiceId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_INVOICES]);
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DRAW]);
        setShowRemoveConfirm(false);
        setShowDeleteModal(false);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Invoice deleted successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const deleteInvoiceHandler = () => {
    removeInvoice.mutate(Number(selectedInvoice?.id));
  };

  const onSelectedRowChange = (selectedIds: Record<string, boolean>) => {
    const selectedInvoice: ISelectedInvoice[] = [];

    list.forEach((invoice, index) => {
      if (Object.hasOwn(selectedIds, index.toString())) {
        selectedInvoice.push({ ...invoice, selectedId: index });
      }
    });

    setSelectedInvoices(selectedInvoice);
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'createdBy',
      header: () => <div className='flex items-start'>Created By</div>,
      accessorKey: 'createdBy',
      cell: (props) => {
        const { getValue } = props;
        return <div className='flex items-start'>{getValue() as string}</div>;
      },
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      cell: (props) => {
        const { getValue } = props;
        return <p className=''>${formatNumber(getValue() as number)}</p>;
      },
    },
    {
      id: 'invoice',
      header: 'Invoice#',
      accessorKey: 'invoiceNumber',
      cell: (props) => {
        const { getValue, row } = props;
        return (
          <Link className='text-blue-600 hover:underline' to={`${location.pathname}/invoice/${row.original.id}`}>
            {getValue() as number}
          </Link>
        );
      },
    },
    {
      id: 'approver',
      header: 'Approver',
      accessorKey: 'approver',
      cell: (props) => {
        const { getValue } = props;
        return <p className=''>{getValue() as string}</p>;
      },
    },

    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (props) => {
        const { getValue } = props;
        return <Badge status={getValue() as string} text={convertEnumToText(getValue() as string)} />;
      },
    },
    {
      id: 'trashIcon',
      header: '',
      accessorKey: 'trashIcon',
      size: 20,
      cell: (props) => {
        const { getValue, row } = props;
        return (
          getValue() && (
            <p>
              <TrashIcon
                className='w-5 h-5 hover:text-tertiaryColor-normal text-neutralColor-normal'
                onClick={() =>
                  removeInvoiceHandler({
                    id: row.original.id,
                    invoiceNumber: row.original.invoiceNumber,
                  })
                }
              />
            </p>
          )
        );
      },
    },
  ];

  const nextPage = () => {
    const nextPage = params.page + 1;
    if (nextPage <= pageCount) {
      setParams((prev: any) => ({ ...prev, page: nextPage }));
    }
  };

  const previousPage = () => {
    const previousPage = params.page - 1;
    if (previousPage > 0) {
      setParams((prev: any) => ({ ...prev, page: previousPage }));
    }
  };

  const tablePagination = {
    pageCount,
    pageSize: params.perPage,
    currentPage: params.page,
    page: params.page,
    setPageSize: (pageSize: number) =>
      setParams((params: any) => ({
        ...params,
        perPage: pageSize,
      })),
    nextPage,
    previousPage,
  };

  const isApprovedStatus = () => {
    return selectedInvoices.every((doc) => doc.status === 'APPROVED');
  };

  const checkSameStatus = () => {
    return selectedInvoices.every((doc) => doc.status === selectedInvoices[0]?.status);
  };

  const openUserModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Invoice.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Invoices should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowUserModal(true);
  };

  const openRejectionModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Invoice.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Invoices should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowRejectModal(true);
  };

  const openApprovalModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Invoice.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Invoices should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowApproveModal(true);
  };

  const openDeleteModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Document.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Invoices should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowDeleteModal(true);
  };

  const cancelUserModel = () => {
    setShowUserModal(false);
  };

  const cancelRejectionModel = () => {
    setShowRejectModal(false);
  };

  const cancelApprovalModel = () => {
    setShowApproveModal(false);
  };

  const cancelDeleteModel = () => {
    setShowDeleteModal(false);
  };

  const menuItems: DropDownMenuItem[] = Object.keys(ApprovalActions).map((key) => {
    return {
      clicked: () => {
        if (key === 'DELEGATED') {
          openUserModal();
        } else if (key === 'REJECTED') {
          openRejectionModal();
        } else if (key === 'APPROVED') {
          openApprovalModal();
        } else if (key === 'DELETE') {
          openDeleteModal();
        }
      },
      name: ApprovalActions[key as keyof typeof ApprovalActions],
    };
  });

  const updateApprovalMutation = useMutation(
    async (payload: UpdateApprovalPayload) => {
      await updateApproval(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_INVOICES]);
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DRAW]);

        tableRef.current.toggleAllRowsSelected();
        setSelectedInvoices([]);
        setShowUserModal(false);
        setShowApproveModal(false);
        setShowRejectModal(false);
      },
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to update Approval',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const { mutateAsync: mutateApproval } = updateApprovalMutation;

  const teamMembersQuery = useQuery(
    [SherpaProjectReactQueryKey.GET_PROJECT_MEMBERS, projectId],
    async () => {
      const response = await getProjectTeamMembers({
        projectId: Number(projectId),
      });
      return response.data?.internal || [];
    },
    {
      enabled: !!projectId,
    }
  );

  const { data: teamMembers = [] } = teamMembersQuery;

  const updatedTeamMembers = teamMembers.filter((mem) => mem?.id !== auth?.data?.id);

  const handleSubmit = async () => {
    const promises = selectedInvoices.map((doc) =>
      mutateApproval({
        id: doc.approvalId,
        status: 'DELEGATED',
        assignedUserId: doc?.assignedUserId,
        note: doc?.note,
      })
    );

    await Promise.all(promises);
    setShowUserModal(false);
  };

  const handleRejection = async () => {
    const promises = selectedInvoices.map((doc) =>
      mutateApproval({
        id: doc.approvalId,
        status: 'REJECTED',
        note: doc?.note,
      })
    );

    await Promise.all(promises);
    setShowRejectModal(false);
  };

  const handleApproval = async () => {
    const promises = selectedInvoices.map((doc) =>
      mutateApproval({
        id: doc.approvalId,
        status: 'APPROVED',
      })
    );

    await Promise.all(promises);
    setShowApproveModal(false);
  };

  const handleBulkDelete = async () => {
    const promises = selectedInvoices.map((doc) => removeInvoice.mutateAsync(Number(doc?.id)));

    await Promise.all(promises);
    setShowDeleteModal(false);
  };

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>, d: ISelectedInvoice) => {
    let editedDoc: ISelectedInvoice;

    if (e?.target?.name === 'note') {
      editedDoc = { ...d, note: e?.target?.value };
    }
    if (e?.target?.name === 'assignedUserId') {
      editedDoc = { ...d, assignedUserId: Number(e?.target?.value) };
    }

    const updatedSelectedInvoices = selectedInvoices.map((u) => (u.id !== editedDoc.id ? u : editedDoc));
    setSelectedInvoices(updatedSelectedInvoices);
  };

  return (
    <>
      {showModal && <NewInvoices handleModal={handleModel} projectName={project?.name || ''} />}
      {showRemoveConfirm && (
        <Confirm
          title='Remove Invoice'
          message={`You are about to remove ${selectedInvoice?.invoiceNumber} invoice. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={() => {
            deleteInvoiceHandler();
          }}
          decline={cancelRemoveInvoice}
        />
      )}
      {fetchInvoices.isSuccess && (
        <Accordion>
          <div className='bg-white'>
            <AccordionItemT2
              id='draw-invoices'
              title={
                <div className='flex flex-row justify-between items-center mr-5'>
                  <div className=''>
                    <div className='-ml-3.5 -my-3'>
                      <AccentTitle title='Draw Invoices' />
                    </div>
                    <div className='flex items-center gap-5 pl-1'>
                      <div className='flex gap-1'>
                        <CheckCircleIcon className='w-5 h-5 text-primaryColor-normal' /> <p>Approved Invoices: {totalInvoiceApproved}</p>
                      </div>
                      <div>Total Invoices: {invoiceCounts}</div>
                      <hr className='h-5 bg-neutralColor-light w-0.5' />
                      <div className='flex gap-1'>
                        <p>Approved Invoices: ${kFormatter(totalInvoiceApprovedAmount.toString())}</p>
                      </div>
                      <div>Total Invoices: ${kFormatter(totalInvoiceAmount.toString())}</div>
                    </div>
                  </div>
                  <div className='flex justify-between items-center gap-4'>
                    {selectedInvoices && selectedInvoices.length > 0 && (
                      <DropDown menuItems={menuItems}>
                        <div className='flex gap-2 items-center border p-2 rounded-md w-44 justify-between font-medium'>
                          <span>Bulk Actions</span>
                          <ChevronDownIcon className='h-4 w-4' />
                        </div>
                      </DropDown>
                    )}
                    <Button icon={<PlusIcon />} label='Add' btnStyle='primary' btnType='button' onClick={handleModel} />
                  </div>
                </div>
              }
              customClassName='border-t-2'
            >
              {list && list.length > 0 && (
                <Table
                  ref={tableRef}
                  cols={tableColumns}
                  tableData={list}
                  pagination={tablePagination}
                  enableSelection={true}
                  onSelectedRowChange={onSelectedRowChange}
                />
              )}
              {list && list.length === 0 && (
                <div className='text-center h-screen/2 flex flex-col justify-center items-center'>
                  <InformationCircleIcon className='h-12 w-12 text-tertiaryColor-normal' />
                  <h3 className='mt-1'>You have no any existing project invoices</h3>
                </div>
              )}
            </AccordionItemT2>
          </div>
        </Accordion>
      )}
      {fetchInvoices.isLoading && <div className='w-full h-48 my-4 rounded animate-pulse bg-neutralColor-lighter'></div>}
      {showUserModal && (
        <Modal width='min-w-1/2'>
          <HookFormProvider>
            <HookForm onSubmit={handleSubmit}>
              <p className='text-center'>Are you sure you want to delegate this invoice</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
              <div className='relative overflow-x-auto border rounded-b-md w-full bg-white max-h-72 mt-6'>
                <table className='divide-y divide-gray-200 w-full overflow-y-auto'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Invoice Number</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Reason for Delegation</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Delegate To</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {selectedInvoices?.map((d: ISelectedInvoice) => (
                      <tr key={d.approvalId}>
                        <td className='p-4'>{d?.invoiceNumber}</td>
                        <td className='p-4'>
                          <Input type='text' value={d?.note} name='note' error='' required changed={(e) => inputChangeHandler(e, d)} />
                        </td>
                        <td className='p-4'>
                          <Select name='assignedUserId' onChange={(e) => inputChangeHandler(e, d)} required>
                            <option value=''>Select...</option>
                            {updatedTeamMembers.map((members) => (
                              <option key={members.name} value={members.id}>
                                {members.name}
                              </option>
                            ))}
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedInvoices && selectedInvoices.length === 0 && (
                  <div className='w-full'>
                    <div className='flex flex-col justify-center items-center h-40'>
                      <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
                      <h5 className='mt-1'>Please select any invoice.</h5>
                    </div>
                  </div>
                )}
              </div>
              <div className='flex gap-2 justify-end w-full mt-5'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    cancelUserModel();
                  }}
                />
                <Button
                  label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Delegate'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading || selectedInvoices.length === 0}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showRejectModal && (
        <Modal width='min-w-1/2'>
          <HookFormProvider>
            <HookForm onSubmit={handleRejection}>
              <p className='text-center'>Are you sure you want to reject this invoice</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
              <div className='relative overflow-x-auto border rounded-b-md w-full bg-white max-h-72 mt-6'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Invoice Number</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Reason for Rejection</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {selectedInvoices?.map((d: ISelectedInvoice) => (
                      <tr key={d.approvalId}>
                        <td className='p-4'>{d.invoiceNumber}</td>
                        <td className='p-4'>
                          <Input
                            type='text'
                            value={d?.note}
                            name='note'
                            error=''
                            required={true}
                            changed={(e) => inputChangeHandler(e, d)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedInvoices && selectedInvoices.length === 0 && (
                  <div className='w-full'>
                    <div className='flex flex-col justify-center items-center h-40'>
                      <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
                      <h5 className='mt-1'>Please select any invoice.</h5>
                    </div>
                  </div>
                )}
              </div>
              <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    cancelRejectionModel();
                  }}
                />
                <Button
                  label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Reject'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading || selectedInvoices.length === 0}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showApproveModal && (
        <Modal>
          <HookFormProvider>
            <HookForm onSubmit={handleApproval}>
              <p className='text-center'>Are you sure you want to Approve this invoice</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes </p>

              <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    cancelApprovalModel();
                  }}
                />
                <Button
                  label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Confirm'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading || selectedInvoices.length === 0}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showDeleteModal && (
        <Modal width='min-w-1/2'>
          <HookFormProvider>
            <HookForm onSubmit={handleBulkDelete}>
              <p className='text-center'>Are you sure you want to remove all Invoices?</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
              <div className='relative overflow-x-auto border rounded-b-md w-full bg-white max-h-72 mt-6'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Invoice Name</th>
                      <th className='p-4 w-32 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {selectedInvoices?.map((d: ISelectedInvoice) => (
                      <tr key={d.approvalId}>
                        <td className='p-4'>{d.invoiceNumber}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedInvoices && selectedInvoices.length === 0 && (
                  <div className='w-full'>
                    <div className='flex flex-col justify-center items-center h-40'>
                      <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
                      <h5 className='mt-1'>Please select any invoice.</h5>
                    </div>
                  </div>
                )}
              </div>
              <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={removeInvoice.isLoading}
                  onClick={() => {
                    cancelDeleteModel();
                  }}
                />
                <Button
                  label={`${removeInvoice.isLoading ? 'Please Wait...' : 'Remove'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading || selectedInvoices.length === 0}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
    </>
  );
};
